"use client";

import { memo, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ArrowRight, ChevronDown, Mail, Menu, Phone, Search, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuItem, MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { cn } from "@/lib/utils";
const MENU_TRANSLATIONS: Record<string, string> = {
  home: "首页",
  products: "产品",
  applications: "应用",
  materials: "材料",
  resources: "技术资源",
  "technical resources": "技术资源",
  "technical resources calculators": "技术资源与计算工具",
  "technical resources & calculators": "技术资源与计算工具",
  "quality & supply": "质量与供应",
  "quality and supply": "质量与供应",
  "quality supply": "质量与供应",
  about: "关于我们",
  contact: "联系我们",
  "polymer filtration": "聚合物过滤",
  "chemical & process filtration": "化学与工艺过滤",
  "chemical and process filtration": "化学与工艺过滤",
  "chemical process filtration": "化学与工艺过滤",
  "chemical filtration": "化学过滤",
  "oil, fuel & petrochemical": "油品、燃料及石化过滤",
  "oil, fuel and petrochemical": "油品、燃料及石化过滤",
  "oil fuel petrochemical": "油品、燃料及石化过滤",
  "oil, fuel & petrochemical filtration": "油品、燃料及石化过滤",
  "oil fuel petrochemical filtration": "油品、燃料及石化过滤",
  "oil & fuel filtration": "油品与燃料过滤",
  "oil and fuel filtration": "油品与燃料过滤",
  "oil fuel filtration": "油品与燃料过滤",
  "oil fuel filtration applications": "油品与燃料过滤应用",
  "oil & fuel filtration applications": "油品与燃料过滤应用",
  "oil and fuel filtration applications": "油品与燃料过滤应用",
  "water & liquid filtration": "水与液体过滤",
  "water and liquid filtration": "水与液体过滤",
  "water liquid filtration": "水与液体过滤",
  "liquid filtration": "液体过滤",
  "liquid filtration straining": "液体过滤与滤网",
  "liquid filtration & straining": "液体过滤与滤网",
  "liquid filtration and straining": "液体过滤与滤网",
  "food & pharmaceutical": "食品与医药过滤",
  "food and pharmaceutical": "食品与医药过滤",
  "food pharmaceutical": "食品与医药过滤",
  "food & pharmaceutical filtration": "食品与医药过滤",
  "food and pharmaceutical filtration": "食品与医药过滤",
  "food pharmaceutical filtration": "食品与医药过滤",
  "food processing filtration": "食品加工过滤",
  "pharmaceutical filtration": "医药过滤",
  "screening & separation": "筛分与分离",
  "screening and separation": "筛分与分离",
  "screening   separation": "筛分与分离",
  "screening separation": "筛分与分离",
  "powder particle screening": "粉体与颗粒筛分",
  "powder & particle screening": "粉体与颗粒筛分",
  "powder and particle screening": "粉体与颗粒筛分",
  "solid liquid separation": "固液分离",
  "solid-liquid separation": "固液分离",
  "gas liquid separation": "气液分离",
  "gas-liquid separation": "气液分离",
  "oil gas separation": "油气分离",
  "oil & gas separation": "油气分离",
  "oil and gas separation": "油气分离",
  "petrochemical process filtration": "石化工艺过滤",
  "gas & air filtration": "气体与空气过滤",
  "gas and air filtration": "气体与空气过滤",
  "gas   air filtration": "气体与空气过滤",
  "gas air filtration": "气体与空气过滤",
  "wastewater filtration": "废水过滤",
  "industrial water filtration": "工业用水过滤",
  "plastic recycling filtration": "塑料回收过滤",
  "polymer melt filtration": "聚合物熔体过滤",
  "polymer melt filtration applications": "聚合物熔体过滤应用",
  "extrusion filtration": "挤出过滤",
  "specialty woven mesh": "特种编织丝网",
  "specialty weaves": "特种织造",
  "sintered multilayer mesh": "多层烧结丝网",
  "sintered wire mesh": "烧结丝网",
  "sintered dutch weave mesh": "荷兰编织烧结网",
  "custom sintered mesh": "定制烧结网",
  "filter discs": "滤圆片",
  "screen packs": "组合滤网",
  "filter screens": "过滤网屏",
  "filter packs": "组合滤网",
  "filter cartridges": "滤芯组件",
  "filter elements": "滤芯组件",
  "woven wire mesh": "编织丝网",
  "filter components": "过滤组件",
  "plain weave": "平纹编织",
  "twill weave": "斜纹编织",
  "plain dutch weave": "平纹荷兰编织",
  "twill dutch weave": "斜纹荷兰编织",
  "reverse dutch weave": "反向荷兰编织",
  "five-heddle weave": "五综编织",
  "five heddle weave": "五综编织",
  "heavy-duty woven mesh": "重型编织网",
  "heavy duty woven mesh": "重型编织网",
  "custom woven mesh": "定制编织网",
  "filter cloth": "工业滤布",
  "square mesh": "方孔网",
  "plain weave wire mesh": "平纹编织丝网",
  "twilled weave wire mesh": "斜纹编织丝网",
  "dutch weave wire mesh": "荷兰编织丝网",
  "plain & twilled weave": "平纹及斜纹编织",
  "plain twilled weave": "平纹及斜纹编织",
  "dutch & reverse dutch weave": "荷兰及反向荷兰编织",
  "dutch reverse dutch weave": "荷兰及反向荷兰编织",
  "3-layer": "3层烧结网",
  "3 layer": "3层烧结网",
  "3 layer sintered mesh": "3层烧结丝网",
  "5-layer": "5层烧结网",
  "5 layer": "5层烧结网",
  "multi-layer": "多层复合网",
  "multi layer": "多层复合网",
  custom: "定制规格",
  "wedge wire screen": "约翰逊楔形滤网",
  "wedge wire screens": "约翰逊楔形滤网",
  "flat curved screens": "平板与弧形筛网",
  "flat & curved screens": "平板与弧形筛网",
  "flat and curved screens": "平板与弧形筛网",
  "flat & curved panels": "平板与弧形滤网",
  "flat and curved panels": "平板与弧形滤网",
  "flat   curved panels": "平板与弧形滤网",
  "flat curved panels": "平板与弧形滤网",
  "flat panel screens": "平板滤网",
  "curved screens": "弧形筛网",
  "sieve bend screens": "弧形筛板",
  "cylindrical screens": "圆筒滤网",
  "axial slot cylinders": "轴向缝隙滤筒",
  "circumferential slot cylinders": "环向缝隙滤筒",
  "screen tubes": "筛管",
  "wedge wire tubes": "楔形丝筛管",
  "screen pipes": "筛管",
  "filter nozzles": "滤水帽/喷嘴",
  "filter tubes & cylinders": "滤管与滤筒",
  "filter tubes and cylinders": "滤管与滤筒",
  "filter tubes   cylinders": "滤管与滤筒",
  "filter tubes cylinders": "滤管与滤筒",
  "filter baskets and plates": "滤篮与滤板",
  "filter baskets & plates": "滤篮与滤板",
  "filter baskets plates": "滤篮与滤板",
  "materials grades": "材质等级",
  "materials & grades": "材质等级",
  "materials and grades": "材质等级",
  "materials   grades": "材质等级",
  "materials & surface": "材质与表面处理",
  "materials and surface": "材质与表面处理",
  "materials surface": "材质与表面处理",
  "materials & surface finishes": "材质与表面处理",
  "materials surface finishes": "材质与表面处理",
  "engineering guides & downloads": "工程指南与下载",
  "engineering guides and downloads": "工程指南与下载",
  "engineering guides downloads": "工程指南与下载",
  "technical guides & downloads": "技术指南与下载",
  "technical guides downloads": "技术指南与下载",
  "calculation guides": "计算指南",
  "selection guides": "选型指南",
  "technical charts": "技术图表",
  "pdf downloads": "PDF技术手册",
  "buying guides": "采购指南",
  datasheets: "数据表",
  "standards tolerances inspection": "标准、公差与检测",
  "standards, tolerances & inspection": "标准、公差与检测",
  "standards tolerances & inspection": "标准、公差与检测",
  "standards tolerances and inspection": "标准、公差与检测",
  "standards & inspection": "标准与检测",
  "standards and inspection": "标准与检测",
  "standards   inspection": "标准与检测",
  "standards inspection": "标准与检测",
  "astm e2016 explained": "ASTM E2016标准解析",
  "wire mesh inspection guide": "丝网检测指南",
  "astm e2016 scope & limitations": "ASTM E2016适用范围与限制",
  "astm e2016 scope and limitations": "ASTM E2016适用范围与限制",
  "astm e2016 scope   limitations": "ASTM E2016适用范围与限制",
  "astm e2016 scope limitations": "ASTM E2016适用范围与限制",
  "how wire mesh aperture is measured": "丝网孔径测量方法",
  "iso wire mesh standards": "ISO丝网标准",
  "stainless steel material verification": "不锈钢材质鉴定",
  "engineering calculations": "工程计算",
  "engineering   calculations": "工程计算",
  comparisons: "规格对比",
  "product selection": "产品选型",
  "engineering guides": "工程指南",
  "applications & processes": "应用与工艺",
  "applications and processes": "应用与工艺",
  "applications   processes": "应用与工艺",
  "applications processes": "应用与工艺",
  "specifications & standards": "规格与标准",
  "specifications and standards": "规格与标准",
  "specifications   standards": "规格与标准",
  "specifications standards": "规格与标准",
  "specifications & selection": "规格与选型",
  "specifications selection": "规格与选型",
  downloads: "下载专区",
  "sintered wire mesh selection guide": "烧结丝网选型指南",
  "filter mesh selection guide": "过滤丝网选型指南",
  "wedge wire screen selection guide": "楔形滤网选型指南",
  "filter components selection guide": "过滤组件选型指南",
  "stainless steel grade selection": "不锈钢牌号选型",
  "stainless steel filter mesh materials": "不锈钢过滤网材料",
  "material compatibility guide": "材料耐蚀兼容指南",
  "wire mesh specifications": "丝网规格参数",
  "filter mesh specifications": "过滤网规格参数",
  "wedge wire specifications": "楔形滤网规格参数",
  "aperture micron rating charts": "孔径与微米等级图表",
  "aperture   micron rating charts": "孔径与微米等级图表",
  "corrosion resistance matrices": "耐腐蚀性矩阵",
  "for extrusion filtration": "挤出过滤应用",
  "for polymer melt filtration": "聚合物熔体过滤应用",
  "for liquid filtration": "液体过滤应用",
  "for screening & separation": "筛分与分离应用",
  "for screening and separation": "筛分与分离应用",
  "for screening   separation": "筛分与分离应用",
  "for screening separation": "筛分与分离应用",
  "product catalog": "产品目录",
  capabilities: "制造能力",
  sitemap: "网站地图",
  "mesh count & aperture": "目数与孔径",
  "mesh count and aperture": "目数与孔径",
  "mesh count aperture": "目数与孔径",
  "mesh fundamentals": "丝网基础知识",
  "stainless steel mesh": "不锈钢丝网",
  "industrial applications": "工业应用",
  "alloy specifications": "合金规格"
};
function normalizeMenuKey(text: string): string {
  if (!text) return "";
  return text.replace(/&#038;|&amp;/gi, "&").toLowerCase().replace(/[&,]/g, " ").replace(/\band\b/g, " ").replace(/[-_]/g, " ").replace(/\s+/g, " ").trim();
}
function getMenuLabel(label: string, lang: string): string {
  if (lang !== "zh" || !label) return label;
  const decoded = label.replace(/&#038;|&amp;/gi, "&").trim();
  const normalizedKey = normalizeMenuKey(decoded);
  const rawKey = decoded.toLowerCase().replace(/\s+/g, " ");
  return MENU_TRANSLATIONS[normalizedKey] || MENU_TRANSLATIONS[rawKey] || MENU_TRANSLATIONS[label.trim().toLowerCase()] || label;
}
const ProductsMenuTree = memo(function ProductsMenuTree({
  items,
  lang = "en"
}: {
  items: MenuItem[];
  lang?: string;
}) {
  const multiTierItems = items.filter(item => item.children?.some(child => child.children && child.children.length > 0));
  const flatTierItems = items.filter(item => !item.children?.some(child => child.children && child.children.length > 0));
  return <div className="max-h-[75vh] overflow-y-auto w-[680px] lg:w-[760px] max-w-[92vw] bg-popover/95 text-popover-foreground rounded-xl ring-1 ring-border/50 shadow-2xl backdrop-blur-sm p-4">
      {/* Top Block: multiTierItems */}
      {multiTierItems.length > 0 && <div className="space-y-3">
          {multiTierItems.map((cat, catIdx) => {
        const subcategories = cat.children || [];
        return <div key={cat.id} data-index={catIdx} className="space-y-2">
                {/* Full-width Category Heading */}
                <div>
                  <NavigationMenuLink asChild>
                    <Link to={cat.href || "#"} className="flex items-center px-2 py-1 text-sm font-bold text-foreground hover:text-primary hover:bg-accent/70 rounded-md transition-colors duration-150 border-b border-primary/20 pb-1.5 mb-1">
                      {getMenuLabel(cat.label, lang)}
                    </Link>
                  </NavigationMenuLink>
                </div>

                {/* Direct Children (Subcategories) as Responsive Columns */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {subcategories.map((sub, subIdx) => {
              const leaves = sub.children || [];
              return <div key={sub.id} data-index={subIdx} className={cn("flex flex-col", subIdx % 3 !== 0 && "sm:border-l sm:border-border/50 sm:pl-3")}>
                        <NavigationMenuLink asChild>
                          <Link to={sub.href || "#"} className="flex items-center px-2 py-1 text-sm font-semibold text-foreground/90 hover:text-primary hover:bg-accent/60 rounded-md transition-colors duration-150 mb-1">
                            {getMenuLabel(sub.label, lang)}
                          </Link>
                        </NavigationMenuLink>

                        {leaves.length > 0 && <div className="flex flex-col gap-0.5">
                            {leaves.map((leaf, leafIdx) => <div key={leaf.id} data-index={leafIdx}>
                                <NavigationMenuLink asChild>
                                  <Link to={leaf.href || "#"} className="flex items-center px-2 py-1 text-xs text-foreground/70 hover:text-primary hover:bg-accent/50 rounded-md transition-colors duration-150 truncate">
                                    {getMenuLabel(leaf.label, lang)}
                                  </Link>
                                </NavigationMenuLink>
                              </div>)}
                          </div>}
                      </div>;
            })}
                </div>
              </div>;
      })}
        </div>}

      {/* Bottom Block: flatTierItems */}
      {flatTierItems.length > 0 && <div className={cn(multiTierItems.length > 0 && "border-t border-border/60 pt-3 mt-3")}>
          <div className={cn("grid gap-3", flatTierItems.length === 1 ? "grid-cols-1" : flatTierItems.length === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-3")}>
            {flatTierItems.map((cat, catIdx) => {
          const leaves = cat.children || [];
          const colCount = Math.min(flatTierItems.length, 3);
          return <div key={cat.id} data-index={catIdx} className={cn("flex flex-col", catIdx % colCount !== 0 && "sm:border-l sm:border-border/50 sm:pl-3")}>
                  <div>
                    <NavigationMenuLink asChild>
                      <Link to={cat.href || "#"} className="flex items-center px-2 py-1 text-sm font-bold text-foreground hover:text-primary hover:bg-accent/70 rounded-md transition-colors duration-150 border-b border-primary/20 pb-1.5 mb-1">
                        {getMenuLabel(cat.label, lang)}
                      </Link>
                    </NavigationMenuLink>
                  </div>

                  {leaves.length > 0 && <div className="flex flex-col gap-1">
                      {leaves.map((leaf, leafIdx) => <div key={leaf.id} data-index={leafIdx}>
                          <NavigationMenuLink asChild>
                            <Link to={leaf.href || "#"} className="flex items-center px-2 py-1 text-xs text-foreground/70 hover:text-primary hover:bg-accent/50 rounded-md transition-colors duration-150 truncate">
                              {getMenuLabel(leaf.label, lang)}
                            </Link>
                          </NavigationMenuLink>
                        </div>)}
                    </div>}
                </div>;
        })}
          </div>
        </div>}
    </div>;
});
const DesktopMenuSubitems = memo(function DesktopMenuSubitems({
  items,
  parentItem,
  depth = 1,
  lang = "en"
}: {
  items: MenuItem[];
  parentItem?: MenuItem;
  depth?: number;
  lang?: string;
}) {
  if (depth === 1) {
    const parentKey = parentItem?.label ? parentItem.label.trim().toLowerCase() : "";
    const isAppsOrResources = parentKey === "applications" || parentKey === "resources";
    const hasBranchingChildren = items.some(item => item.children && item.children.length > 0);
    const isMegaMenu = isAppsOrResources || hasBranchingChildren || items.length >= 7;
    const gridCols = isAppsOrResources ? "grid-cols-3 w-[900px] max-w-[94vw]" : items.length >= 9 ? "grid-cols-3 w-[880px] max-w-[90vw]" : isMegaMenu ? "grid-cols-2 w-[620px] max-w-[90vw]" : "grid-cols-1 w-64";
    return <div className={cn("bg-popover/95 text-popover-foreground p-4 rounded-xl shadow-2xl ring-1 ring-border/50 border-t border-border backdrop-blur-sm transition-all max-h-[75vh] overflow-y-auto", gridCols)}>
        <div className={cn("grid gap-4", isAppsOrResources || items.length >= 9 ? "grid-cols-3" : isMegaMenu ? "grid-cols-2" : "grid-cols-1")}>
          {items.map((cat, i) => {
          const hasChildren = cat.children && cat.children.length > 0;
          return <div key={cat.id} data-index={i} className="flex flex-col">
                <NavigationMenuLink asChild>
                  <Link to={cat.href || "#"} className={cn("flex items-center px-2 py-1 text-sm font-bold text-foreground hover:text-primary hover:bg-accent/70 rounded-md transition-colors duration-150", hasChildren && "border-b border-primary/20 pb-1.5 mb-1")}>
                    <span className="truncate">{getMenuLabel(cat.label, lang)}</span>
                  </Link>
                </NavigationMenuLink>

                {hasChildren && <DesktopMenuSubitems items={cat.children!} parentItem={cat} depth={2} lang={lang} />}
              </div>;
        })}
        </div>
      </div>;
  }
  const isDepth2 = depth === 2;
  return <ul className={cn("flex flex-col gap-0.5", isDepth2 ? "bg-muted/30 border-l-2 border-primary/30 rounded-md p-1 ml-1 mt-0.5" : "bg-muted/50 border-l border-border rounded-md p-0.5 ml-2 mt-0.5")}>
      {items.map((child, i) => {
      const hasGrandChildren = child.children && child.children.length > 0;
      return <li key={child.id} data-index={i} className="flex flex-col">
            <NavigationMenuLink asChild>
              <Link to={child.href || "#"} className={cn("flex items-center rounded-md px-2 py-1 transition-colors duration-150", isDepth2 ? "text-xs font-medium text-foreground/85 hover:text-primary hover:bg-accent/60" : "text-xs font-normal text-foreground/75 hover:text-primary hover:bg-accent/50")}>
                {!isDepth2 && <span className="size-1 rounded-lg bg-primary/50 shrink-0 mr-1.5" />}
                <span className="truncate">{getMenuLabel(child.label, lang)}</span>
              </Link>
            </NavigationMenuLink>
            {depth <= 2 && hasGrandChildren && <DesktopMenuSubitems items={child.children!} parentItem={child} depth={depth + 1} lang={lang} />}
          </li>;
    })}
    </ul>;
});
const DesktopNavigationConsumer = memo(function DesktopNavigationConsumer({
  lang = "en"
}: {
  lang?: string;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const navigate = useNavigate();
  const goToPage = (href?: string) => {
    if (!href || href === "#") return;
    if (href.startsWith("http://") || href.startsWith("https://")) {
      window.location.href = href;
    } else {
      navigate(href);
    }
  };
  if (loading) {
    return <div className="flex items-center gap-2">
        <div className="h-8 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="flex items-center gap-1.5">
        {menuItems.map((item, i) => {
        const itemKey = item.label.trim().toLowerCase();
        const isProducts = itemKey === "products";
        const isApplications = itemKey === "applications";
        const isResources = itemKey === "resources";
        const isWideDropdown = isProducts || isApplications || isResources;
        return <NavigationMenuItem key={item.id} data-index={i}>
              {item.children && item.children.length > 0 ? <>
                  <NavigationMenuTrigger onClick={() => goToPage(item.href)} className="text-foreground hover:text-primary data-[state=open]:text-primary font-medium text-sm transition-all duration-200 rounded-md px-3.5 py-2 [&_svg]:transition-transform [&_svg]:duration-200">
                    {getMenuLabel(item.label, lang)}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className={cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 duration-200 data-[state=open]:translate-y-1 data-[state=closed]:translate-y-0", isWideDropdown ? "fixed left-1/2 -translate-x-1/2 top-[72px] sm:top-[80px] max-w-[94vw]" : "left-1/2 -translate-x-1/2")}>
                    {isProducts ? <ProductsMenuTree items={item.children} lang={lang} /> : <DesktopMenuSubitems items={item.children} parentItem={item} depth={1} lang={lang} />}
                  </NavigationMenuContent>
                </> : <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="px-3.5 py-2 text-sm font-medium text-foreground hover:text-primary transition-all inline-block rounded-md">
                    {getMenuLabel(item.label, lang)}
                  </Link>
                </NavigationMenuLink>}
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
});
const MobileMenuTree = memo(function MobileMenuTree({
  items,
  onNavigate,
  depth = 1,
  lang = "en"
}: {
  items: MenuItem[];
  onNavigate: () => void;
  depth?: number;
  lang?: string;
}) {
  const isTop = depth === 1;
  const isDepth2 = depth === 2;
  return <Accordion type="multiple" className={cn("w-full flex flex-col", isTop ? "gap-1" : isDepth2 ? "gap-1 pl-2.5 ml-1 mt-1 border-l-2 border-primary/30 bg-muted/20 rounded-md p-1.5" : "gap-1 pl-2 ml-1.5 mt-1 border-l border-border bg-muted/30 rounded-md p-1")}>
      {items.map((item, i) => {
      const hasChildren = item.children && item.children.length > 0;
      if (hasChildren) {
        const isNavigable = Boolean(item.href && item.href !== "#");
        return <AccordionItem key={item.id} value={item.id} data-index={i} className={cn("border-b-0", isTop && "border-b border-border/30 pb-1 mb-0.5")}>
              <AccordionPrimitive.Header className="flex items-center justify-between w-full">
                {isNavigable ? <Link to={item.href || "#"} onClick={onNavigate} className={cn("flex-1 min-w-0 py-2 px-2.5 rounded-md transition-colors duration-150 text-left flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1", isTop ? "text-sm font-bold text-foreground hover:bg-accent/70 hover:text-primary" : isDepth2 ? "text-xs font-medium text-foreground/90 hover:bg-accent/80 hover:text-primary" : "text-xs font-normal text-foreground/75 hover:bg-muted/80 hover:text-primary")}>
                    {!isTop && !isDepth2 && <span className="size-1 rounded-lg bg-primary/50 shrink-0 mr-1.5" />}
                    <span className="truncate block">{getMenuLabel(item.label, lang)}</span>
                  </Link> : <span className={cn("flex-1 min-w-0 py-2 px-2.5 rounded-md transition-colors duration-150 text-left select-none flex items-center", isTop ? "text-sm font-bold text-foreground hover:bg-accent/70 hover:text-primary" : isDepth2 ? "text-xs font-medium text-foreground/90 hover:bg-accent/80 hover:text-primary" : "text-xs font-normal text-foreground/75 hover:bg-muted/80 hover:text-primary")}>
                    {!isTop && !isDepth2 && <span className="size-1 rounded-lg bg-primary/50 shrink-0 mr-1.5" />}
                    <span className="truncate block">{getMenuLabel(item.label, lang)}</span>
                  </span>}
                <AccordionPrimitive.Trigger aria-label={`Toggle ${item.label} submenu`} className="p-2 rounded-md text-muted-foreground hover:text-primary hover:bg-accent/80 transition-colors duration-150 shrink-0 [&[data-state=open]>svg]:rotate-180 outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1">
                  <ChevronDown className="size-4 shrink-0 transition-transform duration-200" />
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>
              <AccordionContent className="pb-1 pt-0.5">
                <MobileMenuTree items={item.children!} onNavigate={onNavigate} depth={depth + 1} lang={lang} />
              </AccordionContent>
            </AccordionItem>;
      }
      return <div key={item.id} data-index={i} className={cn("flex flex-col", isTop && "border-b border-border/30 pb-0.5 mb-0.5")}>
            <Link to={item.href || "#"} onClick={onNavigate} className={cn("flex items-center rounded-md transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1", isTop ? "py-2 px-2.5 text-sm font-bold text-foreground hover:bg-accent/70 hover:text-primary" : isDepth2 ? "py-1.5 px-2 text-xs font-medium text-foreground/90 hover:bg-accent/80 hover:text-primary" : "py-1 px-2 text-xs font-normal text-foreground/75 hover:bg-muted/80 hover:text-primary")}>
              {!isTop && !isDepth2 && <span className="size-1 rounded-lg bg-primary/50 shrink-0 mr-1.5" />}
              <span className="truncate">{getMenuLabel(item.label, lang)}</span>
            </Link>
          </div>;
    })}
    </Accordion>;
});
const MobileNavigationConsumer = memo(function MobileNavigationConsumer({
  onNavigate,
  lang = "en"
}: {
  onNavigate: () => void;
  lang?: string;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 py-4">
        <div className="h-7 w-3/4 bg-muted animate-pulse rounded-md" />
        <div className="h-7 w-2/3 bg-muted animate-pulse rounded-md" />
        <div className="h-7 w-4/5 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <MobileMenuTree items={menuItems} onNavigate={onNavigate} depth={1} lang={lang} />;
});
export default function Navigation() {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");
  const [lang, setLang] = useState<string>(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("mf_lang") || "en";
    }
    return "en";
  });
  const handleLanguageSwitch = useCallback((nextLang: string) => {
    setLang(nextLang);
    if (typeof window !== "undefined") {
      localStorage.setItem("mf_lang", nextLang);
      window.dispatchEvent(new CustomEvent("mf-language-change", {
        detail: {
          lang: nextLang
        }
      }));
    }
  }, []);
  const toggleLanguage = useCallback(() => {
    const next = lang === "en" ? "zh" : "en";
    handleLanguageSwitch(next);
  }, [lang, handleLanguageSwitch]);
  useEffect(() => {
    const handleCustomLangChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: string;
      }>;
      if (customEvent.detail?.lang) {
        setLang(customEvent.detail.lang);
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "mf_lang" && e.newValue) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("mf-language-change", handleCustomLangChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("mf-language-change", handleCustomLangChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const executeSearch = (query: string) => {
    if (!query.trim()) return;
    window.location.href = `/SearchResults?q=${encodeURIComponent(query.trim())}`;
  };
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <MenuProvider menu_id="4">
      <section id="main-navigation" data-nav="light" className="sticky top-0 z-50 w-full bg-background border-b border-border transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-18 sm:h-20 flex items-center justify-between gap-4">
          <div className="flex items-center shrink-0 min-w-0">
            <Link to="/" className="flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm" aria-label="MutualFilter Home">
              <WvcLogo className="h-10 sm:h-12 lg:h-14 w-auto max-w-[170px] sm:max-w-[200px] lg:max-w-[240px] object-contain !text-[rgba(18,24,33,1)] [&_svg]:h-10 [&_svg]:sm:h-12 [&_svg]:lg:h-14 [&_svg]:w-auto [&_svg]:max-w-[170px] [&_svg]:sm:max-w-[200px] [&_svg]:lg:max-w-[240px] [&_svg]:object-contain [&_svg]:!text-[rgba(18,24,33,1)]" />
            </Link>
          </div>

          <div className="hidden lg:flex items-center justify-center flex-1 px-4">
            <DesktopNavigationConsumer lang={lang} />
          </div>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            {isSearchOpen ? <div className="flex items-center bg-card border border-input rounded-md px-2.5 py-1 shadow-xs animate-in fade-in duration-200">
                <Search className="size-4 text-muted-foreground mr-1.5 shrink-0" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                executeSearch(searchQuery);
              }
            }} placeholder={lang === "zh" ? "搜索产品、网格..." : "Search products, mesh..."} className="w-44 text-sm bg-transparent outline-none text-foreground placeholder:text-muted-foreground" autoFocus />
                <Button type="button" variant="ghost" size="sm" onClick={() => executeSearch(searchQuery)} className="h-7 px-2 text-xs font-semibold text-primary hover:bg-primary/10">
                  {lang === "zh" ? "搜索" : "Search"}
                </Button>
                <Button type="button" variant="ghost" size="icon" onClick={() => {
              setIsSearchOpen(false);
              setSearchQuery("");
            }} className="size-7 text-muted-foreground hover:text-foreground" aria-label={lang === "zh" ? "关闭搜索" : "Close search"}>
                  <X className="size-3.5" />
                </Button>
              </div> : <Button type="button" variant="outline" size="icon" onClick={() => setIsSearchOpen(true)} className="size-9 border-border text-foreground hover:text-primary hover:bg-accent/80" aria-label={lang === "zh" ? "打开搜索" : "Open search bar"}>
                <Search className="size-4" />
              </Button>}

            <Button type="button" variant="outline" size="sm" onClick={toggleLanguage} className="h-9 px-2.5 border-border text-xs font-semibold text-foreground hover:bg-accent/80 hover:text-primary transition-colors flex items-center gap-1 select-none" aria-label="Language selector">
              <span className={cn(lang === "en" ? "text-primary font-bold" : "text-foreground/60")}>
                EN
              </span>
              <span className="text-border text-[10px]">|</span>
              <span className={cn(lang === "zh" ? "text-primary font-bold" : "text-foreground/60")}>
                中文
              </span>
            </Button>

            <Button asChild variant="default" size="default" className="font-semibold px-5 shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 transition-all">
              <Link to="/RequestAQuotePage" className="flex items-center gap-2">
                <span>{lang === "zh" ? "获取报价" : "Request a Quote"}</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="size-10 border-primary/40 text-primary hover:bg-primary/10 hover:text-primary transition-colors" aria-label={lang === "zh" ? "切换导航菜单" : "Toggle navigation menu"}>
                  <Menu className="size-5 text-primary" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-sm flex flex-col justify-between p-6 bg-background border-l border-border">
                <div className="space-y-6">
                  <SheetHeader className="p-0 text-left border-b border-border pb-4 pt-1 flex flex-row items-center justify-between pr-8">
                    <SheetTitle className="flex items-center">
                      <Link to="/" onClick={() => setIsMobileOpen(false)} className="focus-visible:outline-none">
                        <WvcLogo className="h-7 sm:h-8 w-auto max-w-[150px] object-contain !text-[rgba(18,24,33,1)] [&_svg]:h-7 [&_svg]:sm:h-8 [&_svg]:w-auto [&_svg]:max-w-[150px] [&_svg]:object-contain [&_svg]:!text-[rgba(18,24,33,1)]" />
                      </Link>
                    </SheetTitle>
                    <Button type="button" variant="outline" size="sm" onClick={toggleLanguage} className="h-8 px-2 border-border text-xs font-semibold text-foreground hover:bg-accent/80 hover:text-primary transition-colors flex items-center gap-1 select-none shrink-0 mr-1" aria-label="Language selector">
                      <span className={cn(lang === "en" ? "text-primary font-bold" : "text-foreground/60")}>
                        EN
                      </span>
                      <span className="text-border text-[10px]">|</span>
                      <span className={cn(lang === "zh" ? "text-primary font-bold" : "text-foreground/60")}>
                        中文
                      </span>
                    </Button>
                  </SheetHeader>

                  <div className="flex items-center bg-card border border-input rounded-md px-3 py-2 shadow-xs">
                    <Search className="size-4 text-muted-foreground mr-2 shrink-0" />
                    <input type="text" value={mobileSearchQuery} onChange={e => setMobileSearchQuery(e.target.value)} onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setIsMobileOpen(false);
                      executeSearch(mobileSearchQuery);
                    }
                  }} placeholder={lang === "zh" ? "搜索钢丝网、规格..." : "Search wire mesh, specs..."} className="w-full text-sm bg-transparent outline-none text-foreground placeholder:text-muted-foreground" />
                    <Button type="button" variant="ghost" size="sm" onClick={() => {
                    setIsMobileOpen(false);
                    executeSearch(mobileSearchQuery);
                  }} className="h-7 px-2 text-xs font-semibold text-primary hover:bg-primary/10 shrink-0">
                      {lang === "zh" ? "前往" : "Go"}
                    </Button>
                  </div>

                  <div className="overflow-y-auto max-h-[calc(100vh-340px)] pr-1">
                    {isMobileOpen && <MobileNavigationConsumer onNavigate={() => setIsMobileOpen(false)} lang={lang} />}
                  </div>
                </div>

                <div className="pt-6 border-t border-border space-y-4">
                  <Button asChild variant="default" className="w-full justify-center font-semibold shadow-md shadow-primary/20">
                    <Link to="/RequestAQuotePage" onClick={() => setIsMobileOpen(false)} className="flex items-center justify-center gap-2">
                      <span>{lang === "zh" ? "获取报价" : "Request a Quote"}</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>

                  <div className="space-y-2 pt-2 text-xs text-muted-foreground font-mono">
                    <div className="flex items-center gap-2">
                      <Phone className="size-3.5 text-primary" />
                      <a href="tel:+8618033666533" className="hover:text-foreground transition-colors">
                        (86) 180-3366-6533
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="size-3.5 text-primary" />
                      <a href="mailto:sales@mutualfilter.com" className="hover:text-foreground transition-colors">
                        sales@mutualfilter.com
                      </a>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </section>
    </MenuProvider>;
}